import { createFileRoute } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { SiteFooter } from "@/components/SiteFooter";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";

import { BUSINESS_FAQ_CATEGORIES } from "@/content/business-corpus/faq-defs";


export const Route = createFileRoute("/en/business/faq/")({
  loader: async (): Promise<{ BUSINESS_FAQ_EN: { q: string; a: string }[]; MASTER_KEYWORDS_ALL: string[]; MASTER_QUESTIONS_EN: string[] }> => {
    const [m0, m1] = await Promise.all([
      import("@/content/business-corpus/categories"),
      import("@/content/master-corpus"),
    ]);
    return { BUSINESS_FAQ_EN: m0.BUSINESS_FAQ_EN, MASTER_KEYWORDS_ALL: m1.MASTER_KEYWORDS_ALL, MASTER_QUESTIONS_EN: m1.MASTER_QUESTIONS_EN };
  },
  component: BusinessFaqHubEn,
  head: ({ loaderData }) => {
    const { BUSINESS_FAQ_EN = [], MASTER_KEYWORDS_ALL = [], MASTER_QUESTIONS_EN = [] } = loaderData ?? {};

    const site = getActiveSiteUrl();
    return {
      ...pageHeadForProvince({
        title: "Business, BOI, Tax & Expat FAQ Thailand 2026 — Attorney Answers",
        description:
          "Interpreters for Thai government and court appointments, BOI document sets, M&A legalisation, audited statements, tax residency and expat VIP relocation — answered by Thai attorneys.",
        path: "/en/business/faq",
        type: "article",
        alternates: [{ lang: "th", path: "/business/faq" }],
        keywords: MASTER_KEYWORDS_ALL.slice(0, 80),
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "Home", url: `${site}/en` },
              { name: "Business & Expat FAQ", url: `${site}/en/business/faq` },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(BUSINESS_FAQ_EN)) },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            inLanguage: "en",
            speakable: { "@type": "SpeakableSpecification", cssSelector: ["h1", "h2", ".faq-answer"] },
          }),
        },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema("/en/business/faq")) },
      ],
    };
  },
});

function BusinessFaqHubEn() {
  const { BUSINESS_FAQ_EN, MASTER_KEYWORDS_ALL, MASTER_QUESTIONS_EN } = Route.useLoaderData() as { BUSINESS_FAQ_EN: { q: string; a: string }[]; MASTER_KEYWORDS_ALL: string[]; MASTER_QUESTIONS_EN: string[] };
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-4xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/en" className="hover:underline">Home</a> <span>/</span> <span>Business &amp; Expat FAQ</span>
        </nav>
        <h1 className="text-3xl md:text-4xl font-bold mb-3">
          Business, BOI, Tax &amp; Expat FAQ — {BUSINESS_FAQ_EN.length} answers (2026)
        </h1>
        <p className="text-muted-foreground mb-8">
          Practical answers for foreign investors and relocating executives: certified interpreters at the
          Land Office, district office and court; BOI and work permit document sets; M&amp;A legalisation;
          audited statements and tax certificates; and end-to-end relocation support.
        </p>

        <div className="mb-6 rounded-lg border bg-muted/30 p-4 text-sm">
          <strong>ภาษาไทย:</strong>{" "}
          <a href="/business/faq" className="text-primary underline">FAQ ธุรกิจและนักลงทุน →</a>
        </div>

        <section aria-label="Business FAQ categories" className="mb-8">
          <h2 className="text-lg font-semibold mb-3">Browse by topic</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {BUSINESS_FAQ_CATEGORIES.map((c) => (
              <a
                key={c.slug}
                href={`/en/business/faq/${c.slug}`}
                className="rounded-lg border bg-card p-3 text-sm transition-colors hover:border-primary hover:bg-primary/5"
              >
                <div className="font-semibold mb-1">{c.titleEn} →</div>
                <div className="text-xs text-muted-foreground line-clamp-2">{c.descEn}</div>
              </a>
            ))}
          </div>
        </section>

        <div className="space-y-4">
          {BUSINESS_FAQ_EN.map((item, i) => (
            <details key={i} className="rounded-lg border bg-card p-4 open:shadow-sm" {...(i < 5 ? { open: true } : {})}>
              <summary className="cursor-pointer font-semibold">
                <span className="text-muted-foreground mr-2">Q{i + 1}.</span>{item.q}
              </summary>
              <p className="faq-answer mt-3 text-sm leading-relaxed text-muted-foreground">{item.a}</p>
            </details>
          ))}
        </div>

        <section className="mt-12 rounded-lg border p-6 bg-primary/5">
          <h2 className="text-xl font-bold mb-2">One team for the whole corporate file</h2>
          <p className="text-sm text-muted-foreground mb-4">
            Pre-check → certified translation → attorney certification / MFA legalisation → Apostille 2026 →
            DBD, BOI and Immigration filings, with an interpreter at every appointment.
          </p>
          <a href="/contact" className="inline-block rounded-md bg-primary px-5 py-2 text-primary-foreground font-medium">
            Talk to our corporate desk →
          </a>
        </section>

        <section className="mt-12">
          <h2 className="text-xl font-bold mb-3">Popular searches ({MASTER_QUESTIONS_EN.length} questions)</h2>
          <ul className="grid gap-1 text-sm text-muted-foreground md:grid-cols-2">
            {MASTER_QUESTIONS_EN.slice(0, 150).map((q, i) => (
              <li key={i}>• {q}</li>
            ))}
          </ul>
        </section>
      
        <FaqSourceNote topic="business" locale="en" />
      </main>
      <SiteFooter />
    </>
  );
}